import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { useDebounce } from '../../hooks/useDebounce';

interface SearchSuggestion {
  value: string;
  type: 'title' | 'author' | 'publisher' | 'category';
  count: number;
}

interface SearchBarProps {
  placeholder?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  onSearch?: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search books, authors, or keywords...',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  onSearch,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [loading] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [debouncedQuery]);

  const fetchSuggestions = async (searchQuery: string) => {
    // No backend endpoint exists for suggestions; keep this a no-op to avoid
    // firing requests that always 404.
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSearch = (searchQuery: string = query) => {
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      if (onSearch) {
        onSearch(searchQuery.trim());
      } else {
        navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) {
      if (event.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSearch(suggestions[selectedIndex].value);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.value);
    handleSearch(suggestion.value);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'title':
        return '📖';
      case 'author':
        return '👤';
      case 'publisher':
        return '🏢';
      case 'category':
        return '📚';
      default:
        return '🔍';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'title':
        return 'Book';
      case 'author':
        return 'Author';
      case 'publisher':
        return 'Publisher';
      case 'category':
        return 'Category';
      default:
        return 'Search';
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={anchorRef} sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          ref={inputRef}
          fullWidth={fullWidth}
          variant={variant}
          size={size}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          InputProps={{
            startAdornment: (
              <IconButton
                onClick={() => handleSearch()}
                edge="start"
                aria-label="search"
                disabled={!query.trim()}
              >
                <Search />
              </IconButton>
            ),
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                {query && (
                  <IconButton
                    onClick={handleClear}
                    edge="end"
                    aria-label="clear"
                    size="small"
                  >
                    <Clear />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />

        <Popper
          open={showSuggestions}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ 
            zIndex: 1300, 
            width: anchorRef.current?.offsetWidth || 'auto',
            maxWidth: '100vw'
          }}
        >
          <Paper elevation={8} sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List dense disablePadding>
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={`${suggestion.type}-${suggestion.value}`}
                  button
                  selected={index === selectedIndex}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography component="span">
                          {getTypeIcon(suggestion.type)}
                        </Typography>
                        <Typography component="span" variant="body2">
                          {suggestion.value}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {getTypeLabel(suggestion.type)} • {suggestion.count} result{suggestion.count !== 1 ? 's' : ''}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchBar;